import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { packageStatus } from "../../utils/packageStatus";
import { iconList } from "../../utils/iconList";
import LeafletMap from "../map/leafletMap";
import { markerHtml } from "../map/orderMapMarkers";
import moment from "moment";
import { getOnePackage, getPackageLogs } from "../../api/packages";
import { getUserById } from "../../api/user";
import { getParisLabel } from "../../api/integrations";
import { formatCLP, formatMoney, formatVolume } from "../../utils/formatters";
import { CopyButton } from "../ui/CopyButton";
import { Button } from "../ui/Button";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { trackingLink } from "../../views/adminView/notifications/whatsapp/broadcastMessage";
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  Download,
  MapPin,
  Package,
  Calendar,
  Phone,
  FileText,
  User,
  Clock,
  AlertCircle,
  XCircle,
  MessageSquare,
  ArrowLeft,
  BookOpen,
  AlertTriangle,
  Weight,
  Hash,
  Truck,
  X,
  QrCode,
  MessageCircle,
  Mail,
  Boxes,
  Store,
  ShieldCheck,
  Banknote,
  Wallet,
  Receipt,
  ArrowRightLeft,
  Pencil,
  Ban,
  PhoneCall,
  RotateCw,
  ClipboardCopy,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import MediaLightbox from "../helpdesk/MediaLightbox";
import { showToast } from "../../utils/showToast";
import useAuth from "../../hooks/useAuth";
import { INCIDENT_DESK_ROLE_IDS, roleRoutes } from "../../utils/roles";
import PackageLogbook from "../packageLogbook";
import Modal from "../modal";
import { Modal as UiModal } from "../ui/Modal";
import DownloadVoucher from "../downloadVoucher";
import PackageAiDetailModal from "../../views/adminView/deliveryQuality/components/PackageAiDetailModal";
import deliveryQualityService from "../../services/deliveryQualityService";
import EditOrderDrawer from "../editOrderDrawer/editOrderDrawer";
import CancelPackage from "../cancelPackage";
import EditDeliveryAddressModal from "../editDeliveryAddress/EditDeliveryAddressModal";
import { originKey } from "../OriginIcon";
import { canCancelOrder, canEditDeliveryAddress, canEditOrder } from "../../utils/orderActions";
import { normalizeCommune } from "../../utils/communes";
import { ResolutionSummary } from "./ResolutionSummary";
import ExchangeDetails, { isExchangeRelated } from "./ExchangeDetails";
import {
  buildResolution,
  cleanEvidenceValue,
  codMethodMap,
  codStatusMap,
  evidenceForStatus,
  failedIndexByEntry,
  failedOptionMap,
  formatDistance,
  getUserDisplayName,
  isCallablePhone,
  mapPoints,
  personName,
  planActions,
  returnReasonMap,
  sortStatusHistory,
} from "./resolution";
import "./evidenceView.scss";

// Estado numérico → clase del pill (igual que el listado de Pedidos).
const PD_STATUS_CLASS = { 0: "pending", 1: "picked", 2: "picked", 3: "route", 4: "delivered", 5: "cancelled", 6: "failed", 7: "failed", 30: "route", 100: "storage" };

// ============ UTILITY FUNCTIONS ============

// ⚠️ ROTO EN PRODUCCIÓN — no funciona y no es trivial de arreglar desde el front.
// `fetch(img)` es cross-origin contra s3-api.beartrackapp.com, que responde 200
// pero SIN cabecera `Access-Control-Allow-Origin` (verificado con curl el
// 2026-07-23, incluso mandando Origin del portal). El browser bloquea la
// respuesta y el ZIP nunca se genera. Sobrevive SOLO por el botón "Descargar
// todas" de la sección Sobredimensionado; el de Prueba de entrega ya se migró a
// la descarga del comprobante PDF. Para repararlo hay que exponer ACAO en
// s3-api (Transform Rule de Cloudflare o el propio servicio), no en este archivo.
const downloadImagesAsZip = async (images, followingNumber) => {
  const zip = new JSZip();
  for (const [index, img] of images.entries()) {
    const response = await fetch(img);
    const blob = await response.blob();
    zip.file(`${followingNumber}-evidence-${index + 1}.jpg`, blob, { binary: true });
  }
  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, `${followingNumber}-evidence.zip`);
};

const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, '')}`;

// Calle y número de una dirección; sin número, la línea completa.
const streetLine = (address) =>
  address?.street_number ? `${address.street_name}, ${address.street_number}` : address?.address_line;

// Formatea dimensiones { length, width, height } → "L×W×H cm" (omite vacíos).
const dimStr = (d) => {
  if (!d) return null;
  const dims = [d.length, d.width, d.height]
    .filter((x) => x != null && x !== "")
    .join("×");
  return dims ? `${dims} cm` : null;
};

// Formatea volumen en cm³ con separador de miles.
const fmtCm3 = (v) => formatVolume(Math.round(v));

const oversizedReasonMap = {
  weight: 'Peso excedido',
  size: 'Dimensiones excedidas',
  volume: 'Volumen excedido',
  fragile: 'Frágil',
  other: 'Otro'
};

const oversizedCategoryMap = {
  XL: 'XL – Sobredimensionado leve',
  '2XL': '2XL – Sobredimensionado moderado',
  '3XL': '3XL – Sobredimensionado severo',
};

// ── Pago contra entrega (COD) ────────────────────────────────────────────────
// Las etiquetas de estado y medio viven en resolution.js porque también las usa
// la franja de resolución; acá quedan las que sólo usa la sección de cobro.

// Hacia dónde se movió el dinero. Es LA distinción que importa: define si el
// cobro entra en la rendición del repartidor y en la compensación al comercio,
// o si el operador nunca lo tocó. Sin esta línea, "Transferencia al vendedor" se
// lee igual que las otras dos y el comercio espera una compensación que no viene.
const codMoneyDestinationMap = {
  cash: 'El repartidor recibió el dinero: se rinde al operador y el operador lo compensa al comercio.',
  transfer_driver:
    'El repartidor recibió el dinero: se rinde al operador y el operador lo compensa al comercio.',
  transfer_seller:
    'El dinero fue directo a la cuenta del comercio: el operador no lo recauda, solo queda la evidencia.',
};

const codSourceMap = {
  manual: 'Cargado a mano al crear el pedido',
  excel: 'Columna «Cobrar» de la carga masiva',
  // Ninguna plataforma e-commerce expone hoy el medio de pago: el monto se
  // infiere del estado de pago del pedido y puede ser un falso positivo.
  'integration-derived': 'Derivado del pedido de la integración (propuesto)',
};

// Origen del pedido — MISMOS íconos que el listado (ModernOrdersList.renderPackageTypeIcon),
// para coherencia entre la lista y el detalle. Excel/OTS incluidos (faltaban: el detalle
// caía al ícono genérico). Excel usa Font Awesome igual que la lista.
const ORIGIN_MAP = {
  MERCADOLIBRE: { label: 'MercadoLibre', icon: iconList.ml },
  SHOPIFY: { label: 'Shopify', icon: iconList.shopify },
  JUMPSELLER: { label: 'Jumpseller', icon: iconList.jumpsellerIcon },
  FALABELLA: { label: 'Falabella', icon: iconList.falabella },
  RIPLEY: { label: 'Ripley', icon: iconList.ripley },
  WOOCOMMERCE: { label: 'WooCommerce', icon: iconList.woo },
  PRESTASHOP: { label: 'Prestashop', icon: iconList.prestashop },
  SENDU: { label: 'Sendu', icon: iconList.sendu },
  OTS: { label: 'Manual', icon: iconList.box }, // pedido creado a mano (type "OTS") -> "Manual"
  WIX: { label: 'Wix', icon: iconList.wix },
  PARIS: { label: 'Paris', icon: iconList.paris },
  EXCEL: { label: 'Excel', excel: true },
};

// ============ SUB-COMPONENTS ============

// Encabezado de sección estilo mockup (ícono brand + título + descripción).
// Exportado para reusar el mismo lenguaje visual (pd-card) en otras vistas
// (p.ej. el detalle de parada en Actividad/LiveRoutes) sin reinventar.
export const SectionHeader = ({ icon, title, description, badge }) => (
  <div className="pd-section-header">
    <div className="pd-section-header__main">
      <span className="pd-section-header__icon">{icon}</span>
      <div>
        <h3 className="pd-section-header__title">{title}</h3>
        {description && <p className="pd-section-header__desc">{description}</p>}
      </div>
    </div>
    {badge}
  </div>
);

// Campo etiqueta/valor con ícono. Exportado para reuso (ver SectionHeader).
export const Field = ({ icon, label, children, wide }) => (
  <div className={`pd-field${wide ? ' pd-field--wide' : ''}`}>
    <span className="pd-field__icon">{icon}</span>
    <div className="pd-field__body">
      <span className="pd-field__label">{label}</span>
      <span className="pd-field__value">{children}</span>
    </div>
  </div>
);

const OriginBadge = ({ type }) => {
  const o = ORIGIN_MAP[type?.toUpperCase()];
  if (!o) {
    return <span className="pd-origin"><Package size={13} />{type || 'Manual'}</span>;
  }
  if (o.excel) {
    return (
      <span className="pd-origin">
        <i className="fas fa-file-excel pd-origin__excel-icon" aria-hidden="true" />
        {o.label}
      </span>
    );
  }
  return (
    <span className="pd-origin">
      <img src={o.icon} alt={o.label} className="pd-origin__logo" />
      {o.label}
    </span>
  );
};

// Imágenes de evidencia (reutilizado en sobredimensionado)
const EvidenceImages = ({ images, followingNumber, title, onOpen }) => {
  const { t } = useTranslation();
  if (!images || images.length === 0) return null;
  return (
    <div className="evidence-section">
      <div className="evidence-section__header">
        <h3 className="evidence-section__title">{title}</h3>
        {images.length > 1 && (
          <button onClick={() => downloadImagesAsZip(images, followingNumber)} className="download-all-btn">
            <Download size={16} />
            {t("Descargar todas")}
          </button>
        )}
      </div>
      <div className="evidence-images">
        {images.map((image, index) => (
          <button
            key={index}
            type="button"
            className="evidence-image-wrapper"
            onClick={() => onOpen?.(images, index)}
            aria-label={t("Ampliar {{title}} {{current}} de {{total}}", { title: title.toLowerCase(), current: index + 1, total: images.length })}
          >
            <img src={image} alt={`${title} ${index + 1}`} className="evidence-image" />
          </button>
        ))}
      </div>
    </div>
  );
};

// Modal de contacto del repartidor (solo admin). Muestra teléfono para llamar
// o escribir por WhatsApp; el teléfono no viene en `assignTo`, se obtiene aparte.
const DriverContactModal = ({ open, onClose, loading, driver, name }) => {
  const { t } = useTranslation();
  if (!open) return null;

  const phone = driver?.phone;
  const altPhone = driver?.aditionalPhone;
  const email = driver?.email;
  const displayName = (name || `${driver?.name || ''} ${driver?.lastName || ''}`.trim() || t("Repartidor"));
  const initials = displayName.substring(0, 2).toUpperCase();
  const waHref = (p) => `https://wa.me/${String(p).replace(/[^\d]/g, '')}`;

  return (
    <UiModal
      open={open}
      onClose={onClose}
      size="sm"
      ariaLabel={t("Contacto del repartidor")}
      hideClose
      className="pd-contact pd-contact--ui"
      bodyClassName="pd-contact__body-wrap p-0"
    >
      <div className="pd-contact__panel" style={{ position: "static", maxHeight: "none" }}>
        <div className="pd-contact__head">
          <div className="pd-contact__id">
            <span className="pd-contact__avatar">{initials}</span>
            <div>
              <h3 className="pd-contact__name">{displayName}</h3>
              <span className="pd-contact__role">{t("Repartidor asignado")}</span>
            </div>
          </div>
          <button className="pd-contact__close" onClick={onClose} aria-label={t("Cerrar")}>
            <X size={18} />
          </button>
        </div>

        <div className="pd-contact__body">
          {loading ? (
            <div className="pd-contact__state">{t("Cargando datos de contacto…")}</div>
          ) : phone ? (
            <>
              <div className="pd-contact__rows">
                <div className="pd-contact__row">
                  <span className="pd-contact__label"><Phone size={14} />{t("Teléfono")}</span>
                  <a className="pd-link pd-mono" href={telHref(phone)}>{phone}</a>
                </div>
                {altPhone && (
                  <div className="pd-contact__row">
                    <span className="pd-contact__label"><Phone size={14} />{t("Teléfono adicional")}</span>
                    <a className="pd-link pd-mono" href={telHref(altPhone)}>{altPhone}</a>
                  </div>
                )}
                {email && (
                  <div className="pd-contact__row">
                    <span className="pd-contact__label"><Mail size={14} />{t("Correo")}</span>
                    <a className="pd-link" href={`mailto:${email}`}>{email}</a>
                  </div>
                )}
              </div>
              <div className="pd-contact__actions">
                <a className="pd-contact__btn pd-contact__btn--primary" href={telHref(phone)}>
                  <Phone size={15} />{t("Llamar")}
                </a>
                <a className="pd-contact__btn" href={waHref(phone)} target="_blank" rel="noreferrer">
                  <MessageCircle size={15} />WhatsApp
                </a>
              </div>
            </>
          ) : (
            <div className="pd-contact__state pd-contact__state--empty">
              {t("Este repartidor no tiene teléfono registrado.")}
            </div>
          )}
        </div>
      </div>
    </UiModal>
  );
};

// ============ MAIN COMPONENT ============

// `onPackageChanged`: lo llama el detalle después de editar o cancelar el pedido,
// para que la lista de la que se vino se refresque al volver.
// `incidentOrigin` y `onIncidentCreated` (opcionales): se reenvían a la
// Auditoría (PackageLogbook) para que una incidencia creada desde ahí lleve su
// origen y avise al contenedor (p. ej. la vista "Atención e incidencias").
export default function EvidenceView({
  selectedPackage,
  setViewEvidence,
  stacked = false,
  onPackageChanged,
  incidentOrigin,
  onIncidentCreated,
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === roleRoutes.admin || user?.role === roleRoutes.support;
  // Solo para la Auditoría (PackageLogbook): los 4 roles de la mesa de
  // incidencias, el mismo gate que aplica PackageLogbook. El resto de esta vista
  // (calidad, contacto del repartidor) sigue con isAdmin.
  const canAudit = INCIDENT_DESK_ROLE_IDS.includes(user?.role);
  const isHolding = user?.role === roleRoutes.holdingAdmin;
  const [evidencePackage, setEvidencePackage] = useState(null);
  const [loading, setLoading] = useState(true);
  // Se incrementa con "Reintentar" para volver a correr la carga inicial.
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [addressEditOpen, setAddressEditOpen] = useState(false);
  const [isLogbookOpen, setIsLogbookOpen] = useState(false);
  const [showQuality, setShowQuality] = useState(false);
  // Etiqueta de Paris: busy del botón y confirmación antes de imprimirla en Paris.
  const [parisLabelBusy, setParisLabelBusy] = useState(false);
  const [parisPrintConfirm, setParisPrintConfirm] = useState(false);
  const [quality, setQuality] = useState(null); // { totalScore, verdict } | 'none' | null
  const [showLabel, setShowLabel] = useState(false);
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [driverLoading, setDriverLoading] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);
  // Quién reportó el sobredimensionado cuando el detalle trae `overSizedEvidence.updatedBy`
  // sin poblar (solo el ObjectId): se toma del log `oversized` más reciente del paquete.
  const [oversizedLogAuthor, setOversizedLogAuthor] = useState(null);
  // Visor de evidencia: reusa MediaLightbox (helpdesk) en modo galería.
  // { images: string[], index: number } | null
  const [lightbox, setLightbox] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  // Punto elegido en el mapa del pedido: { key, nonce, fromMap }. `nonce` deja
  // volver a pedir el mismo punto; `fromMap` = lo clickearon en el mapa y no hay
  // que moverlo.
  const [mapSelected, setMapSelected] = useState(null);
  const mapCardRef = useRef(null);

  const openLightbox = (images, index = 0) => {
    const list = (Array.isArray(images) ? images : [images]).filter(Boolean);
    if (!list.length) return;
    setLightbox({ images: list, index: Math.min(Math.max(index, 0), list.length - 1) });
  };

  const packageId = selectedPackage?.correlative?.id;

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    getOnePackage(packageId).then((res) => {
      if (isMounted && res.success) {
        setEvidencePackage(res.data);
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
    return () => { isMounted = false; };
  }, [packageId, loadAttempt]);

  // Después de editar o cancelar: se recarga SIN volver al skeleton, para que la
  // vista no parpadee. Si falla, queda lo que había y se avisa; mostrar el error
  // de carga completo por un refresco fallido borraría información válida.
  const refreshPackage = useCallback(async () => {
    try {
      const res = await getOnePackage(packageId);
      if (res?.success) {
        setEvidencePackage(res.data);
        return;
      }
    } catch (err) {
      console.error('[evidenceView] No se pudo refrescar el pedido', err);
    }
    showToast(t("No se pudo actualizar el pedido. Vuelve a abrirlo para ver su estado."), 'error');
  }, [packageId, t]);

  const handleOrderChanged = useCallback(() => {
    refreshPackage();
    onPackageChanged?.();
  }, [refreshPackage, onPackageChanged]);

  const oversizedReason = evidencePackage?.overSizedEvidence?.reason;
  const oversizedUpdatedBy = evidencePackage?.overSizedEvidence?.updatedBy;
  const oversizedAuthorFromPackage = getUserDisplayName({ updatedBy: oversizedUpdatedBy });
  useEffect(() => {
    setOversizedLogAuthor(null);
    if (!evidencePackage?._id || !oversizedReason?.trim() || oversizedAuthorFromPackage) return undefined;
    let alive = true;
    getPackageLogs(evidencePackage._id, { silent: true }).then((res) => {
      if (!alive || !res.success || !Array.isArray(res.data)) return;
      // Logs vienen ordenados por createdAt desc: el primero `oversized` es el reporte vigente.
      const log = res.data.find((entry) => entry?.type === 'oversized' && entry.createdBy);
      if (log) setOversizedLogAuthor(getUserDisplayName({ updatedBy: log.createdBy }));
    });
    return () => { alive = false; };
  }, [evidencePackage?._id, oversizedReason, oversizedAuthorFromPackage]);
  const oversizedAuthor = oversizedAuthorFromPackage || oversizedLogAuthor;

  // Score de calidad (delivery-quality) del paquete, para el badge inline y el
  // botón "Ver evaluación". Vive en un doc aparte (getScoreByPackage); 404 = sin
  // evaluación (no es error). Solo admin.
  useEffect(() => {
    // Solo los pedidos ENTREGADOS (status 4) se evalúan: para el resto no pedimos
    // score ni mostramos el botón. 404 = entregado pero la IA aún no lo procesó
    // (corre en cola, hay delay entre entregado y evaluado).
    if (!isAdmin || evidencePackage?.status !== 4 || !evidencePackage?._id) { setQuality(null); return undefined; }
    let alive = true;
    deliveryQualityService
      .getScoreByPackage(evidencePackage._id)
      .then((r) => {
        if (!alive) return;
        const d = r?.data || {};
        setQuality({ totalScore: d.totalScore ?? null, verdict: d.scores?.imagesAi?.verdict || null });
      })
      .catch((e) => { if (alive) setQuality(e?.response?.status === 404 ? 'none' : null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, evidencePackage?.status, evidencePackage?._id]);

  // Historial de estados ordenado (más reciente primero) = bitácora
  const sortedStatusHistory = useMemo(
    () => sortStatusHistory(evidencePackage?.packageStatusHistory),
    [evidencePackage],
  );

  const sortedFailedHistory = useMemo(
    () => sortStatusHistory(evidencePackage?.packageStatusHistoryFailed),
    [evidencePackage],
  );

  // Asocia cada entrada fallida (status 6/7) con su intento en failedAttempts.
  const failedStatusIndices = useMemo(() => failedIndexByEntry(sortedStatusHistory), [sortedStatusHistory]);

  // Evidencia (imágenes + coords) asociada a cada estado del historial.
  const getEvidenceForStatus = (status, entryId) =>
    evidenceForStatus(evidencePackage, status, failedStatusIndices.get(entryId) ?? 0);

  // Dirección, intentos fallidos y entrega: todo lo que va al mapa del pedido.
  const rawMapPoints = useMemo(
    () => mapPoints(evidencePackage, sortedStatusHistory),
    [sortedStatusHistory, evidencePackage],
  );
  // Apilado (live-routes) no hay mapa: los enlaces "Ver en el mapa" no se muestran.
  const mapKeys = useMemo(
    () => new Set(stacked ? [] : rawMapPoints.map((p) => p.key)),
    [rawMapPoints, stacked],
  );

  // Centra el mapa en un punto y lo abre. Con `scroll` (bitácora, franja) además
  // lleva la vista al mapa. Reemplaza los enlaces a Google Maps: la dirección,
  // los intentos y la entrega se comparan en el mismo mapa.
  const focusMapPoint = useCallback((key, { scroll = true } = {}) => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    if (scroll) mapCardRef.current?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
    setMapSelected({ key, nonce: Date.now(), reduceMotion });
  }, []);

  const resolution = useMemo(
    () => (evidencePackage ? buildResolution(evidencePackage, sortedStatusHistory) : null),
    [sortedStatusHistory, evidencePackage],
  );

  // Prueba de entrega (sólo si fue entregado / hay evidencia de entrega).
  const deliveredEntry = sortedStatusHistory.find((e) => e.status === 4);
  const pod = (evidencePackage?.images?.length || deliveredEntry)
    ? {
        images: evidencePackage?.images || [],
        receiver: evidencePackage?.realReceiver,
        coords: evidencePackage?.coords,
        at: deliveredEntry?.updatedAt,
      }
    : null;

  if (loading) {
    return (
      <div className="package-detail-skeleton">
        <div className="package-detail-skeleton__header">
          <div className="package-detail-skeleton__title" />
          <div className="package-detail-skeleton__badge" />
        </div>
        {/* Misma forma que la vista cargada (franja + bitácora y datos), para
            que al llegar los datos no salte el layout. */}
        <div className="package-detail-skeleton__content">
          <div className="package-detail-skeleton__card package-detail-skeleton__band">
            <div className="package-detail-skeleton__photos">
              {[1, 2, 3].map((i) => <div key={i} className="package-detail-skeleton__photo" />)}
            </div>
            <div className="package-detail-skeleton__content-item">
              <div className="package-detail-skeleton__label" />
              <div className="package-detail-skeleton__value" />
              <div className="package-detail-skeleton__value" />
            </div>
          </div>
          <div className="package-detail-skeleton__grid">
            {[1, 2].map((card) => (
              <div key={card} className="package-detail-skeleton__card">
                <div className="package-detail-skeleton__card-title" />
                <div className="package-detail-skeleton__divider" />
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="package-detail-skeleton__item">
                    <div className="package-detail-skeleton__icon" />
                    <div className="package-detail-skeleton__content-item">
                      <div className="package-detail-skeleton__label" />
                      <div className="package-detail-skeleton__value" />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!evidencePackage) {
    return (
      <div className="package-detail-empty">
        <AlertCircle size={48} />
        <p>{t("No se pudo cargar la información del paquete")}</p>
        <button type="button" className="pd-btn-ghost" onClick={() => setLoadAttempt((n) => n + 1)}>
          <RotateCw size={14} aria-hidden="true" />
          {t("Reintentar")}
        </button>
      </div>
    );
  }

  const pkg = evidencePackage;
  const sa = pkg.shipping_address;
  // 'N/D' (dato que el operador configuró no pedir) y 'N/A' (algunos pedidos de
  // MercadoLibre) se tratan como ausentes: mostrarlos haría parecer que alguien
  // escribió basura. Ver cleanEvidenceValue en resolution.js.
  const receiverName = personName(pkg.receiver_name, pkg.receiver_lastName);
  const podReceiverName = pod?.receiver ? personName(pod.receiver.name, pod.receiver.lastName) : '';
  const podReceiver = podReceiverName || '—';
  const podReceiverDoc = cleanEvidenceValue(pkg.realReceiver?.rut);
  const repartidor = pkg.assignTo ? `${pkg.assignTo.name || ''} ${pkg.assignTo.lastName || ''}`.trim() : null;
  const driverId = pkg.assignTo?._id;
  // Vendedor (quien vende). Mismo chain que el resto de la app (resume.jsx).
  // OJO: deliveryInfo.deliveryBusinessName = transportista, NO el vendedor.
  const seller = pkg.senderUser?.senderBusinessName || pkg.senderInfo?.businessName || null;

  // Solo admin/soporte: abre el modal y obtiene el teléfono del repartidor.
  const openDriverContact = async () => {
    if (!driverId) return;
    setDriverModalOpen(true);
    setDriverLoading(true);
    setDriverInfo(null);
    const u = await getUserById(driverId);
    setDriverLoading(false);
    setDriverInfo(u && !u.error ? u : { phone: null });
  };
  // Comprobante de entrega en PDF (reemplaza al ZIP roto por CORS). El generador
  // vive en un módulo aparte y se carga bajo demanda: jsPDF no entra al bundle
  // principal, solo al chunk que baja al pulsar el botón.
  const handleDownloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const { generateDeliveryReportPdf } = await import("./deliveryReportPdf");
      const res = await generateDeliveryReportPdf({
        pkg,
        pod,
        receiver: podReceiver,
        statusLabel: t(packageStatus[pkg.status]),
      });
      const failed = res?.photos?.failed || 0;
      if (failed > 0) {
        showToast(
          failed === 1
            ? t("Comprobante descargado, pero {{failed}} foto no se pudo incluir.", { failed })
            : t("Comprobante descargado, pero {{failed}} fotos no se pudieron incluir.", { failed }),
          'warning',
        );
      } else {
        showToast(t("Comprobante descargado."), 'success');
      }
    } catch (err) {
      console.error('[evidenceView] No se pudo generar el comprobante PDF', err);
      showToast(t("No se pudo generar el comprobante."), 'error');
    } finally {
      setPdfBusy(false);
    }
  };

  const instrucciones = sa?.comment || pkg.comments;
  const addressLine = streetLine(sa);
  const addressSub = [sa?.municipality?.name, sa?.city?.name, sa?.country?.name].filter(Boolean).join(', ');
  // Dirección corregida después del alta: quién la cambió y cuál era, para que
  // se entienda por qué no coincide con la etiqueta impresa ni con la tienda.
  // Si solo cambió la referencia (o se volvió a la del alta), la original es la
  // misma que la vigente: queda el chip de quién editó, sin repetir la dirección.
  // Se compara calle + número y comuna sin tildes ni mayúsculas, como el backend:
  // la línea del alta suele traer la comuna pegada y la edición la rearma sin ella,
  // y la comuna puede volver con su grafía oficial ("Ñuñoa" por "NUNOA").
  const addressEdit = pkg.addressEdit;
  const streetKey = (address) =>
    normalizeCommune(address?.street_name ? `${address.street_name} ${address.street_number ?? ''}` : address?.address_line);
  const sameDeliveryPoint = (a, b) =>
    streetKey(a) === streetKey(b) && normalizeCommune(a?.municipality?.name) === normalizeCommune(b?.municipality?.name);
  const originalAddress =
    addressEdit?.original && !sameDeliveryPoint(addressEdit.original, sa)
      ? [streetLine(addressEdit.original), addressEdit.original.municipality?.name].filter(Boolean).join(', ')
      : null;
  const canEditAddress = canEditDeliveryAddress(user?.role, pkg.status);
  // Puntos del mapa con sus textos ya traducidos: el mismo título y las mismas
  // líneas sirven al popup del mapa y a la leyenda de abajo.
  const fmtWhen = (at) => (at ? moment(at).format("DD/MM/YY HH:mm") : null);
  const fmtDistance = (distance) =>
    distance ? t("A {{distance}} de la dirección", { distance: formatDistance(distance.meters, i18n?.language) }) : null;
  const pointsOnMap = rawMapPoints.map((p) => {
    switch (p.kind) {
      case "address":
        return {
          ...p,
          title: t("Dirección de entrega"),
          meta: [addressLine, addressSub].filter(Boolean).join(" · "),
          lines: [addressLine, addressSub, p.verified ? null : t("Ubicación sin verificar")],
        };
      case "failed":
        return {
          ...p,
          title: t("Intento fallido {{n}}", { n: p.attempt }),
          meta: [fmtWhen(p.at), p.reason ? t(p.reason) : null].filter(Boolean).join(" · "),
          lines: [fmtWhen(p.at), p.reason ? t(p.reason) : null, fmtDistance(p.distance)],
        };
      case "delivered":
        return {
          ...p,
          title: t("Entrega"),
          meta: [fmtWhen(p.at), podReceiverName ? t("Recibido por {{name}}", { name: podReceiverName }) : null].filter(Boolean).join(" · "),
          lines: [fmtWhen(p.at), podReceiverName ? t("Recibido por {{name}}", { name: podReceiverName }) : null, fmtDistance(p.distance)],
        };
      default:
        return {
          ...p,
          title: t("Devolución completada"),
          meta: fmtWhen(p.at) || "",
          lines: [fmtWhen(p.at), fmtDistance(p.distance)],
        };
    }
  });

  // Contenido del bulto. Estos campos aún no existen en el backend; se leen de
  // forma defensiva y se muestra un empty state hasta que estén disponibles.
  // Contrato esperado (ver BACKEND-PRODUCTOS-BULTO.md).
  const bultoDims = pkg.packageDimensions || null;
  const products = Array.isArray(pkg.products)
    ? pkg.products
    : Array.isArray(pkg.items)
    ? pkg.items
    : [];
  // Valor individual (unitValue, con fallback al legacy `price`).
  const unitValueOf = (p) => {
    const v = p?.unitValue ?? p?.price;
    return v != null && v !== "" ? Number(v) : null;
  };
  // Valor total por línea: usa el del backend si viene; si no, lo calcula
  // (valor individual × cantidad). Ver BACKEND-PRODUCTOS-BULTO.md.
  const lineTotalOf = (p) => {
    if (p?.totalValue != null && p.totalValue !== "") return Number(p.totalValue);
    const uv = unitValueOf(p);
    return uv != null ? uv * (Number(p?.quantity) || 1) : null;
  };
  // Volumen en cm³: usa el del backend si viene; si no, lo calcula (L×W×H).
  const volumeOf = (p) => {
    if (p?.volume != null && p.volume !== "") return Number(p.volume);
    const d = p?.dimensions;
    if (d && d.length != null && d.width != null && d.height != null) {
      const v = Number(d.length) * Number(d.width) * Number(d.height);
      return Number.isFinite(v) ? v : null;
    }
    return null;
  };
  const productsTotal = (() => {
    let sum = 0;
    let any = false;
    for (const p of products) {
      const lt = lineTotalOf(p);
      if (lt != null) {
        any = true;
        sum += lt;
      }
    }
    return any ? sum : null;
  })();
  const declaredValue = pkg.declaredValue != null ? pkg.declaredValue : productsTotal;

  // ── Pago contra entrega (COD) ──────────────────────────────────────────────
  // "Este pedido se cobra" = `cashOnDelivery.amount > 0`. NO existe un flag
  // "habilitado" en el paquete: un pedido sin cobro directamente no lleva el
  // subdocumento.
  //
  // SIN GATE DE ROL, a propósito. Gatear con `isAdmin` dejaría fuera al
  // superadministrador logístico (isAdmin = admin || support, y NO incluye
  // superadmin, que sí pasa el guard de /admin/*), y el comercio tiene que ver
  // monto, medio y comprobante porque es su plata. Como la alternativa sería una
  // lista blanca de 7+ roleIds que hay que recordar actualizar cada vez que nace
  // un rol, la sección se muestra a todo el que ya puede ver el pedido: quien
  // llega a esta vista está autenticado y el backend solo le devuelve paquetes de
  // su tenant. La exposición pública (tracking del destinatario) se corta en el
  // backend, que no serializa `cashOnDelivery` en esos endpoints.
  const cod = pkg.cashOnDelivery || null;
  const codAmount = Number(cod?.amount);
  const hasCod = Number.isFinite(codAmount) && codAmount > 0;
  const codStatus = codStatusMap[cod?.status] || null;
  const codCollectedAmount =
    cod?.collectedAmount != null && cod.collectedAmount !== ""
      ? Number(cod.collectedAmount)
      : null;
  // Diferencia entre lo esperado y lo efectivamente cobrado: no la corrige nadie
  // automáticamente, así que hay que verla en el detalle o se pierde.
  const codAmountMismatch =
    codCollectedAmount != null &&
    Number.isFinite(codCollectedAmount) &&
    codCollectedAmount !== codAmount;
  // `collectedBy` es un ObjectId sin populate en GET /packages/:id: solo se
  // muestra si algún día llega poblado, nunca el id crudo.
  const codCollectedByName = getUserDisplayName({ updatedBy: cod?.collectedBy });

  // ── Acciones ───────────────────────────────────────────────────────────────
  // Qué puede hacer este usuario sobre este pedido. Editar y cancelar siguen
  // las MISMAS reglas que el menú de cada fila en Pedidos (orderActions.js); el
  // resto conserva el gate que ya tenía en esta vista.
  const phoneIsCallable = isCallablePhone(pkg.receiver_phone);
  const availableActions = new Set(["etiqueta"]);
  // El comprobante vale por los datos (receptor, RUT, hora, coords), así que se
  // ofrece siempre que haya prueba de entrega, con 0, 1 o varias fotos.
  if (pod) availableActions.add("comprobante");
  if (isAdmin && pkg.status === 4) availableActions.add("evaluacion");
  if (phoneIsCallable) availableActions.add("llamar");
  if (isAdmin && driverId) availableActions.add("contactar");
  if (canEditOrder(user?.role, pkg.status)) availableActions.add("editar");
  if (canCancelOrder(user?.role, pkg.status)) availableActions.add("cancelar");
  if (canAudit) availableActions.add("auditoria");
  if (originKey(pkg.type) === "PARIS") availableActions.add("etiquetaParis");
  const actionPlan = planActions(resolution.kind, availableActions);

  const ACTIONS = {
    comprobante: {
      label: pdfBusy ? t("Generando…") : t("Descargar comprobante"),
      icon: <Download size={14} aria-hidden="true" />,
      onClick: handleDownloadPdf,
      busy: pdfBusy,
      title: t("Descargar el comprobante de entrega en PDF"),
    },
    evaluacion: {
      label: quality === "none" ? t("IA analizando…") : t("Ver evaluación"),
      icon: <ShieldCheck size={14} aria-hidden="true" />,
      onClick: () => setShowQuality(true),
      title: quality === "none" ? t("La IA aún está analizando esta entrega") : t("Evaluación de calidad de la entrega"),
    },
    llamar: {
      label: t("Llamar al destinatario"),
      icon: <PhoneCall size={14} aria-hidden="true" />,
      href: telHref(pkg.receiver_phone),
    },
    contactar: {
      label: t("Contactar repartidor"),
      icon: <Truck size={14} aria-hidden="true" />,
      onClick: openDriverContact,
    },
    editar: {
      label: t("Editar pedido"),
      icon: <Pencil size={14} aria-hidden="true" />,
      onClick: () => setEditOpen(true),
      title: t("Cambiar el estado o el repartidor"),
    },
    cancelar: {
      label: t("Cancelar pedido"),
      icon: <Ban size={14} aria-hidden="true" />,
      onClick: () => setCancelOpen(true),
    },
    etiqueta: {
      label: t("Ver etiqueta"),
      icon: <QrCode size={14} aria-hidden="true" />,
      onClick: () => setShowLabel(true),
      title: t("Ver e imprimir etiqueta"),
    },
    etiquetaParis: {
      label: parisLabelBusy ? t("Buscando…") : t("Etiqueta Paris"),
      icon: <FileText size={14} aria-hidden="true" />,
      onClick: () => openParisLabel(false),
      busy: parisLabelBusy,
      title: t("Abrir la etiqueta de despacho de Paris (PDF)"),
    },
    auditoria: {
      label: t("Auditoría"),
      icon: <BookOpen size={14} aria-hidden="true" />,
      onClick: () => setIsLogbookOpen(true),
      title: t("Registro de auditoría interno"),
    },
  };
  const toAction = (id) => ({ id, ...ACTIONS[id] });

  // La pestaña se abre ANTES del await: abierta después de una respuesta de red,
  // el navegador la trata como popup y la bloquea.
  async function openParisLabel(print) {
    if (parisLabelBusy) return;
    const tab = window.open("", "_blank");
    setParisLabelBusy(true);
    try {
      const res = await getParisLabel(pkg._id, { print });
      if (res?.labelUrl) {
        setParisPrintConfirm(false);
        if (tab) {
          tab.opener = null;
          tab.location.href = res.labelUrl;
        } else {
          window.location.assign(res.labelUrl);
        }
        if (res.printedNow) {
          showToast(t("Etiqueta impresa en Paris: el pedido quedó listo para despacho."), "success");
        }
        return;
      }
      tab?.close();
      if (res?.status === 409 && res?.error?.code === "PARIS_LABEL_NOT_PRINTED") {
        setParisPrintConfirm(true);
        return;
      }
      showToast(
        (typeof res?.error?.message === "string" && res.error.message) ||
          t("No se pudo obtener la etiqueta de Paris."),
        "error"
      );
    } finally {
      setParisLabelBusy(false);
    }
  }

  // Teléfono del destinatario: enlace sólo si es un número de verdad. El
  // "XXXXXXX" con que MercadoLibre oculta el del comprador se pintaba como
  // enlace `tel:` que no llamaba a nadie.
  const phoneValue = phoneIsCallable ? (
    <a className="pd-link" href={telHref(pkg.receiver_phone)}>{pkg.receiver_phone}</a>
  ) : pkg.receiver_phone && originKey(pkg.type) === "MERCADOLIBRE" ? (
    <span className="pd-muted">{t("Oculto por MercadoLibre")}</span>
  ) : pkg.receiver_phone ? (
    <span className="pd-muted">{pkg.receiver_phone}</span>
  ) : (
    "—"
  );

  // Resumen listo para pegar en WhatsApp: Clarity mostró a clientes que buscan el
  // pedido, copian sus datos a mano y saltan a otra app decenas de veces para
  // responder una consulta. `*…*` es negrita en WhatsApp; las líneas sin dato no van.
  const fullAddress = [addressLine, addressSub].filter(Boolean).join(', ');
  const publicLink = trackingLink(pkg.followingNumber);
  const orderSummary = [
    `*${t("Pedido #{{number}}", { number: pkg.followingNumber })}*`,
    `${t("Estado")}: ${t(packageStatus[pkg.status] || String(pkg.status))}${
      pkg.updatedAt ? ` · ${moment(pkg.updatedAt).format("DD/MM/YYYY HH:mm")}` : ""
    }`,
    receiverName && `${t("Destinatario")}: ${receiverName}`,
    fullAddress && `${t("Dirección")}: ${fullAddress}`,
    pkg.order_id && `${t("Nº venta:")} ${pkg.order_id}`,
    publicLink && `${t("Seguimiento")}: ${publicLink}`,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div className={`pd${stacked ? ' pd--stacked' : ''}`}>
      {/* ============ TOP BAR ============ */}
      <div className="pd__topbar">
        {setViewEvidence && (
          <button className="pd__back" onClick={() => setViewEvidence(false)}>
            <ArrowLeft size={16} />
            {t("Volver")}
          </button>
        )}
        {/* Acciones que no resuelven la pregunta del estado actual: las que sí,
            van destacadas en la franja de abajo (ver planActions). El estado ya
            no va acá: encabeza la franja. */}
        {actionPlan.header.length > 0 && (
          <div className="pd__topbar-actions">
            {actionPlan.header.map(toAction).map((action) => (
              <button
                key={action.id}
                type="button"
                className="pd__audit-btn"
                onClick={action.onClick}
                disabled={action.busy}
                aria-busy={action.busy || undefined}
                title={action.title}
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ============ HEADER ============ */}
      <header className="pd__header flex-wrap">
        <div className="pd__header-icon">
          <Package size={24} />
        </div>
        <div className="pd__header-text">
          {/* El botón va fuera del <h1> para no ensuciar el nombre accesible del título. */}
          <div className="flex items-center gap-1">
            <h1 className="pd__title">{t("Pedido #{{number}}", { number: pkg.followingNumber })}</h1>
            <CopyButton
              value={pkg.followingNumber}
              label={t("Copiar número de seguimiento")}
              copiedMessage={t("Número de seguimiento copiado")}
            />
          </div>
          <p className="pd__subtitle">
            <OriginBadge type={pkg.type} />
            {pkg.order_id && (
              <>
                <span className="pd__dot">·</span>
                <span className="inline-flex items-center gap-0.5">
                  <span>{t("Nº venta:")} <span className="pd-mono">{pkg.order_id}</span></span>
                  <CopyButton
                    value={pkg.order_id}
                    label={t("Copiar número de venta")}
                    copiedMessage={t("Número de venta copiado")}
                  />
                </span>
              </>
            )}
          </p>
        </div>
        <div className="ml-auto shrink-0">
          <Button
            variant="outline"
            size="sm"
            iconLeft={<ClipboardCopy className="w-4 h-4" />}
            onClick={() => copyToClipboard(orderSummary, t("Resumen copiado"), t)}
          >
            {t("Copiar resumen")}
          </Button>
        </div>
      </header>

      {/* ============ FRANJA DE RESOLUCIÓN ============ */}
      <ResolutionSummary
        resolution={resolution}
        statusLabel={t(packageStatus[pkg.status] || String(pkg.status))}
        statusClass={PD_STATUS_CLASS[pkg.status] || "pending"}
        stacked={stacked}
        context={{
          driverName: repartidor,
          sellerName: seller,
          address: { line: addressLine, sub: addressSub, instructions: instrucciones },
          quality: isAdmin && pkg.status === 4 ? quality : null,
          onOpenEventMap: mapKeys.has(resolution.entryKey) ? () => focusMapPoint(resolution.entryKey) : null,
        }}
        actions={actionPlan.band.map(toAction)}
        onOpenPhotos={openLightbox}
      />

      {/* ============ CAMBIO DE PRODUCTO (solo si es padre o hijo de un cambio) ============ */}
      {isExchangeRelated(pkg) && (
        <section className="pd-card">
          <SectionHeader
            icon={<ArrowRightLeft size={18} />}
            title={t("Cambio de producto")}
            description={t("Envío que lleva el producto de reemplazo y retira el original")}
          />
          <ExchangeDetails pkg={pkg} role={user?.role} onOpenPhotos={openLightbox} />
        </section>
      )}

      {/* ============ BITÁCORA + DESTINATARIO Y OPERACIÓN ============ */}
      <div className="pd__grid pd__grid--work">
        {/* BITÁCORA */}
        <section className="pd-card">
          <SectionHeader
            icon={<BookOpen size={18} />}
            title={t("Bitácora")}
            description={sortedStatusHistory.length === 1 ? t("1 evento registrado") : t("{{count}} eventos registrados", { count: sortedStatusHistory.length })}
            badge={<span className="pd-pill pd-pill--neutral">{sortedStatusHistory.length}</span>}
          />
          {sortedStatusHistory.length === 0 ? (
            <div className="pd-empty"><BookOpen size={28} /><p>{t("Sin eventos registrados")}</p></div>
          ) : (
            <ol className="pd-timeline">
              {sortedStatusHistory.map((entry, index) => {
                const ev = getEvidenceForStatus(entry.status, entry._id || entry);
                const isFinal = entry.status === 4 || entry.status === 41;
                const isFailed = entry.status === 5 || entry.status === 6 || entry.status === 7;
                const dot = isFailed ? 'error' : isFinal ? 'success' : 'brand';
                const last = index === sortedStatusHistory.length - 1;
                // El evento que dejó al pedido en su estado actual ya lo cuenta la
                // franja de arriba (fotos, receptor, motivo, ubicación): acá queda
                // sólo quién y cuándo, para no mostrar lo mismo dos veces.
                const coveredByBand = (entry._id || entry) === resolution.entryKey;
                const onMap = mapKeys.has(entry._id || entry);
                return (
                  <li key={entry._id || index} className="pd-timeline__item">
                    {!last && <span className="pd-timeline__line" />}
                    <span className={`pd-timeline__dot pd-timeline__dot--${dot}`} />
                    <div className="pd-timeline__body">
                      <div className="pd-timeline__head">
                        <span className="pd-timeline__status">{t(packageStatus[entry.status])}</span>
                        <span className="pd-timeline__date">{moment(entry.updatedAt).format("DD/MM/YY HH:mm")}</span>
                      </div>
                      {getUserDisplayName(entry) && (
                        <span className="pd-timeline__meta">{t("Por {{name}}", { name: getUserDisplayName(entry) })}</span>
                      )}
                      {entry.status === 4 && !coveredByBand && (podReceiverName || podReceiverDoc) && (
                        <span className="pd-timeline__meta">
                          {podReceiverName ? t("Recibido por {{name}}", { name: podReceiverName }) : ''}
                          {podReceiverName && podReceiverDoc ? ' · ' : ''}
                          {podReceiverDoc}
                        </span>
                      )}
                      {(entry.status === 6 || entry.status === 7) && !coveredByBand && ev.option && (
                        <span className="pd-timeline__reason">
                          <XCircle size={13} />
                          {t(failedOptionMap[ev.option] || ev.option)}
                        </span>
                      )}
                      {(entry.status === 6 || entry.status === 7) && !coveredByBand && ev.selectedOptions?.length > 0 && (
                        <div className="pd-timeline__chips">
                          {ev.selectedOptions.map((opt, k) => (
                            <span key={k} className="pd-timeline__chip">{opt}</span>
                          ))}
                        </div>
                      )}
                      {(entry.status === 6 || entry.status === 7) && !coveredByBand && ev.description && (
                        <span className="pd-timeline__note">{ev.description}</span>
                      )}
                      {entry.status === 22 && !coveredByBand && pkg.returnInfo?.returnReason && (
                        <span className="pd-timeline__meta">{t("Motivo devolución:")} {t(returnReasonMap[pkg.returnInfo.returnReason] || pkg.returnInfo.returnReason)}</span>
                      )}
                      {!coveredByBand && (ev.images?.length > 0 || onMap) && (
                        <div className="pd-timeline__actions">
                          {ev.images?.map((img, k) => (
                            <button
                              key={k}
                              type="button"
                              className="pd-thumb"
                              onClick={() => openLightbox(ev.images, k)}
                              title={t("Ver imagen")}
                              aria-label={t("Ampliar evidencia {{current}} de {{total}} — {{status}}", { current: k + 1, total: ev.images.length, status: t(packageStatus[entry.status]) })}
                            >
                              <img src={img} alt={t("Evidencia {{n}}", { n: k + 1 })} />
                            </button>
                          ))}
                          {onMap && (
                            <button
                              type="button"
                              className="pd-loc-chip"
                              onClick={() => focusMapPoint(entry._id || entry)}
                            >
                              <MapPin size={12} />
                              {t("Ver en el mapa")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {/* DESTINATARIO + COMERCIO Y OPERACIÓN. El origen y el Nº de venta ya
            están en el encabezado; la prueba de entrega, en la franja. */}
        <section className="pd-card">
          <SectionHeader icon={<User size={18} />} title={t("Destinatario")} />
          <div className="pd-fields">
            {/* Nombre, teléfono y dirección se copian todo el día para pegarlos en
                WhatsApp o en un mapa: Clarity mostraba triple clics para seleccionarlos. */}
            <Field icon={<User size={16} />} label={t("Nombre")}>
              {receiverName || '—'}{' '}
              <CopyButton value={receiverName} label={t("Copiar nombre")} copiedMessage={t("Nombre copiado")} />
            </Field>
            <Field icon={<Phone size={16} />} label={t("Teléfono")}>
              {phoneValue}{' '}
              {/* Solo un número real: el "XXXXXXX" de MercadoLibre no sirve copiado. */}
              <CopyButton
                value={phoneIsCallable ? pkg.receiver_phone : null}
                label={t("Copiar teléfono")}
                copiedMessage={t("Teléfono copiado")}
              />
            </Field>
            <Field icon={<MapPin size={16} />} label={t("Dirección de entrega")} wide>
              {addressLine}{' '}
              <CopyButton
                value={[addressLine, addressSub].filter(Boolean).join(', ')}
                label={t("Copiar dirección")}
                copiedMessage={t("Dirección copiada")}
              />
              {addressSub && <span className="pd-field__sub">{addressSub}</span>}
              {addressEdit && (
                <span className="pd-field__sub">
                  <span className="pd-pill pd-pill--warning">
                    {addressEdit.actorKind === 'company' ? t("Editada por el comercio") : t("Editada por el administrador")}
                  </span>
                  {addressEdit.editedAt && ` ${moment(addressEdit.editedAt).format("DD/MM/YYYY HH:mm")}`}
                </span>
              )}
              {originalAddress && (
                <span className="pd-field__sub">{t("Dirección original: {{address}}", { address: originalAddress })}</span>
              )}
              {canEditAddress && (
                <span className="pd-field__sub">
                  <button type="button" className="pd-link pd-driver-link" onClick={() => setAddressEditOpen(true)}>
                    <Pencil size={12} aria-hidden="true" />
                    {t("Editar dirección")}
                  </button>
                </span>
              )}
            </Field>
            <Field icon={<MessageSquare size={16} />} label={t("Instrucciones")} wide>
              {instrucciones || <span className="pd-muted">{t("Sin instrucciones")}</span>}
            </Field>
          </div>

          <div className="pd-card__divider" />

          <SectionHeader icon={<Store size={18} />} title={t("Comercio y operación")} />
          <div className="pd-fields">
            <Field icon={<Store size={16} />} label={t("Vendedor")}>
              {seller || <span className="pd-muted">{t("Sin información")}</span>}
            </Field>
            <Field icon={<Truck size={16} />} label={t("Repartidor")}>
              {repartidor ? (
                isAdmin ? (
                  <button type="button" className="pd-link pd-driver-link" onClick={openDriverContact} title={t("Ver contacto del repartidor")}>
                    {repartidor}
                    <Phone size={12} />
                  </button>
                ) : (
                  repartidor
                )
              ) : (
                <span className="pd-muted">{t("No asignado")}</span>
              )}
            </Field>
            <Field icon={<Calendar size={16} />} label={t("Creación")}>
              {moment(pkg.createdAt).format("DD/MM/YYYY HH:mm")}
            </Field>
            {pkg.collectDate && (
              <Field icon={<Clock size={16} />} label={t("Recolección")}>
                {moment(pkg.collectDate).format("DD/MM/YYYY HH:mm")}
              </Field>
            )}
            {/* Última escritura del documento. Es el único rastro que deja
                sacar un pedido de la ruta: desasignar no toca status ni
                packageStatusHistory (ver unassignPackage en
                microservice-packages), así que cuando esta hora es posterior
                al último evento de la bitácora, aquí está el momento en que el
                pedido salió de la lista del repartidor. */}
            {pkg.updatedAt && (
              <Field icon={<RotateCw size={16} />} label={t("Última actualización")}>
                {moment(pkg.updatedAt).format("DD/MM/YYYY HH:mm")}
              </Field>
            )}
          </div>
        </section>
      </div>

      {/* ============ UBICACIÓN ============
          Debajo de lo que se usa a diario: la distancia a la dirección ya está en
          la franja. Muestra el último punto registrado y, si existe, la dirección
          de entrega, para comparar a ojo. Apilado (live-routes) no se monta: esa
          vista tiene su propio mapa y uno oculto igual cargaba Leaflet. */}
      {!stacked && pointsOnMap.length > 0 && (
        <section className="pd-card pd-card--map" ref={mapCardRef}>
          <div className="pd-card__map-head">
            <SectionHeader
              icon={<MapPin size={18} />}
              title={t("Ubicaciones")}
              description={
                pointsOnMap.some((p) => p.kind !== "address")
                  ? t("Dónde debía entregarse y dónde registró cada intento el repartidor")
                  : t("Dónde debe entregarse el pedido")
              }
            />
          </div>
          <div className="pd-card__map-body">
            <LeafletMap
              points={pointsOnMap}
              selected={mapSelected}
              onSelect={(key) => setMapSelected(key ? { key, nonce: Date.now(), fromMap: true } : null)}
              ariaLabel={t("Mapa de ubicaciones del pedido")}
              id="pd-order-map"
              className="pd-map"
            />
          </div>
          <ol className="pd-map-legend" aria-label={t("Puntos del mapa")}>
            {pointsOnMap.map((p, i) => (
              <li key={`${p.kind}-${i}`}>
                <button
                  type="button"
                  className={`pd-map-legend__item${mapSelected?.key === p.key ? " is-selected" : ""}`}
                  onClick={() => focusMapPoint(p.key, { scroll: false })}
                  title={t("Ver en el mapa")}
                  aria-current={mapSelected?.key === p.key ? "true" : undefined}
                >
                  {/* Mismo marcador que el mapa: HTML fijo de orderMapMarkers + un número. */}
                  <span
                    className={`pd-marker pd-marker--${p.kind} pd-marker--static`}
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: markerHtml(p.kind, p.attempt) }}
                  />
                  <span className="pd-map-legend__text">
                    <span className="pd-map-legend__title">
                      {p.title}
                      {p.kind === "address" && !p.verified && (
                        <span className="pd-pill pd-pill--neutral">{t("Sin verificar")}</span>
                      )}
                      {p.distance?.far && <span className="pd-pill pd-pill--warning">{t("Lejos")}</span>}
                    </span>
                    {p.meta && <span className="pd-map-legend__meta" title={p.meta}>{p.meta}</span>}
                  </span>
                  {p.distance && (
                    <span className="pd-map-legend__distance">{formatDistance(p.distance.meters, i18n?.language)}</span>
                  )}
                </button>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* ============ PAGO CONTRA ENTREGA (solo si el pedido se cobra) ============ */}
      {hasCod && (
        <section className="pd-card">
          <SectionHeader
            icon={<Banknote size={18} />}
            title={t("Pago contra entrega")}
            description={t("Monto que el repartidor cobra al entregar")}
            badge={
              codStatus ? (
                <span className={`pd-pill pd-pill--${codStatus.pill}`}>{t(codStatus.label)}</span>
              ) : null
            }
          />
          <div className="pd-fields">
            <Field icon={<Banknote size={16} />} label={t("Monto a cobrar")}>
              {formatMoney(codAmount, cod.currency)}
            </Field>

            <Field icon={<Wallet size={16} />} label={t("Medio de pago")}>
              {cod.method ? (
                t(codMethodMap[cod.method] || cod.method)
              ) : (
                <span className="pd-muted">{t("Aún no registrado")}</span>
              )}
            </Field>

            {codCollectedAmount != null && (
              <Field icon={<Receipt size={16} />} label={t("Monto cobrado")}>
                {formatMoney(codCollectedAmount, cod.currency)}
                {codAmountMismatch && (
                  <span className="pd-field__sub">
                    {t("Difiere del monto a cobrar ({{amount}}).", { amount: formatMoney(codAmount, cod.currency) })}
                  </span>
                )}
              </Field>
            )}

            {cod.collectedAt && (
              <Field icon={<Clock size={16} />} label={t("Fecha del cobro")}>
                {moment(cod.collectedAt).format("DD/MM/YYYY HH:mm")}
              </Field>
            )}

            {codCollectedByName && (
              <Field icon={<User size={16} />} label={t("Cobrado por")}>
                {codCollectedByName}
              </Field>
            )}

            {cod.source && (
              <Field icon={<Hash size={16} />} label={t("Origen del monto")}>
                {t(codSourceMap[cod.source] || cod.source)}
              </Field>
            )}

            {/* Hacia dónde se movió la plata. Es lo que determina si el operador
                rinde y compensa; sin esto los tres medios se leen igual. */}
            {cod.method && codMoneyDestinationMap[cod.method] && (
              <Field icon={<ArrowRightLeft size={16} />} label={t("Destino del dinero")} wide>
                {t(codMoneyDestinationMap[cod.method])}
              </Field>
            )}

            {/* Solo se muestra cuando ya fue compensado: la ausencia del campo no
                significa "no corresponde", solo que la nota de cobro aún no lo
                incluyó, y afirmar lo contrario sería inventar un estado. */}
            {cod.compensatedIn && (
              <Field icon={<FileText size={16} />} label={t("Compensado al comercio")} wide>
                {t("Incluido en la nota de cobro")}{" "}
                <span className="pd-mono">{String(cod.compensatedIn)}</span>
              </Field>
            )}

            {cod.notes && (
              <Field icon={<MessageSquare size={16} />} label={t("Notas del cobro")} wide>
                {cod.notes}
              </Field>
            )}
          </div>

          {cod.evidenceImages?.length > 0 && (
            <EvidenceImages
              images={cod.evidenceImages}
              followingNumber={pkg.followingNumber}
              title={t("Comprobante de pago")}
            />
          )}
        </section>
      )}

      {/* ============ CONTENIDO DEL BULTO (solo si hay productos, como Sobredimensionado) ============ */}
      {products.length > 0 && (
        <section className="pd-card">
          <SectionHeader
            icon={<Boxes size={18} />}
            title={t("Contenido del bulto")}
            description={t("Productos del pedido para packing y picking")}
            badge={<span className="pd-pill pd-pill--neutral">{products.length}</span>}
          />

          {/* Medidas del bulto + valor total */}
          <div className="pdc-bulto">
            <div className="pdc-chip">
              <span className="pdc-chip__label">{t("Medidas del bulto")}</span>
              <span className="pdc-chip__value">{dimStr(bultoDims) || <span className="pd-muted">{t("Sin medidas")}</span>}</span>
            </div>
            <div className="pdc-chip">
              <span className="pdc-chip__label">{t("Peso")}</span>
              <span className="pdc-chip__value">{bultoDims?.weight ? `${bultoDims.weight} kg` : <span className="pd-muted">—</span>}</span>
            </div>
            <div className="pdc-chip">
              <span className="pdc-chip__label">{t("Valor total")}</span>
              <span className="pdc-chip__value">{declaredValue != null ? formatCLP(declaredValue) : <span className="pd-muted">—</span>}</span>
            </div>
          </div>

          <div className="pdc-table-wrap">
            <table className="pdc-table">
              <thead>
                <tr>
                  <th>{t("Producto")}</th>
                  <th>SKU</th>
                  <th className="pdc-num">{t("Cantidad")}</th>
                  <th className="pdc-num">{t("Peso")}</th>
                  <th className="pdc-num">{t("Volumen")}</th>
                  <th className="pdc-num">{t("Valor individual")}</th>
                  <th className="pdc-num">{t("Valor total")}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => {
                  const uv = unitValueOf(p);
                  const lt = lineTotalOf(p);
                  const vol = volumeOf(p);
                  return (
                    <tr key={p.sku || p._id || i}>
                      <td className="pdc-name">{p.name || p.productName || t("Producto")}</td>
                      <td className="pd-mono">{p.sku || "—"}</td>
                      <td className="pdc-num">{p.quantity ?? 1}</td>
                      <td className="pdc-num">{p.weight != null && p.weight !== "" ? `${p.weight} kg` : "—"}</td>
                      <td className="pdc-num" title={dimStr(p.dimensions) || undefined}>{vol != null ? fmtCm3(vol) : "—"}</td>
                      <td className="pdc-num">{uv != null ? formatCLP(uv) : "—"}</td>
                      <td className="pdc-num">{lt != null ? formatCLP(lt) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
              {productsTotal != null && (
                <tfoot>
                  <tr>
                    <td colSpan={6} className="pdc-total-label">{t("Total")}</td>
                    <td className="pdc-num pdc-total-value">{formatCLP(productsTotal)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      )}

      {/* ============ SOBREDIMENSIONADO (si aplica) ============ */}
      {pkg.overSizedEvidence && pkg.overSizedEvidence.reason && pkg.overSizedEvidence.reason.trim() !== '' && (
        <section className="pd-card pd-card--warn">
          <SectionHeader
            icon={<AlertTriangle size={18} />}
            title={t("Paquete sobredimensionado")}
            description={oversizedAuthor
              ? t("Registrado en bodega por {{name}}", { name: oversizedAuthor })
              : t("Registrado por el equipo de bodega")}
            badge={pkg.overSizedEvidence.category
              ? <span className="pd-pill pd-pill--warning">{pkg.overSizedEvidence.category}</span>
              : null}
          />
          <div className="pd-fields">
            {pkg.overSizedEvidence.category && (
              <Field icon={<Weight size={16} />} label={t("Categoría")}>
                {t(oversizedCategoryMap[pkg.overSizedEvidence.category] || pkg.overSizedEvidence.category)}
              </Field>
            )}
            <Field icon={<AlertCircle size={16} />} label={t("Motivo")}>
              {t(oversizedReasonMap[pkg.overSizedEvidence.reason] || pkg.overSizedEvidence.reason)}
            </Field>
            {pkg.overSizedEvidence.updatedAt && (
              <Field icon={<Clock size={16} />} label={t("Fecha de registro")}>
                {moment(pkg.overSizedEvidence.updatedAt).format("DD/MM/YYYY HH:mm")}
              </Field>
            )}
            {oversizedAuthor && (
              <Field icon={<User size={16} />} label={t("Registrado por")}>
                {oversizedAuthor}
              </Field>
            )}
          </div>
          {pkg.overSizedEvidence.images?.length > 0 && (
            <EvidenceImages
              images={pkg.overSizedEvidence.images}
              followingNumber={pkg.followingNumber}
              title={t("Evidencia de sobredimensionado")}
              onOpen={openLightbox}
            />
          )}
        </section>
      )}

      {/* ============ TRANSICIONES FALLIDAS (admin, interno) ============ */}
      {!isHolding && sortedFailedHistory.length > 0 && (
        <section className="pd-card">
          <SectionHeader
            icon={<XCircle size={18} />}
            title={t("Transiciones de estado fallidas")}
            description={t("Errores internos del sistema")}
          />
          <ol className="pd-timeline">
            {sortedFailedHistory.map((failedEntry, index) => {
              const last = index === sortedFailedHistory.length - 1;
              return (
                <li key={failedEntry._id || index} className="pd-timeline__item">
                  {!last && <span className="pd-timeline__line" />}
                  <span className="pd-timeline__dot pd-timeline__dot--error" />
                  <div className="pd-timeline__body">
                    <div className="pd-timeline__head">
                      <span className="pd-timeline__status">{t("Error")}</span>
                      <span className="pd-timeline__date">{moment(failedEntry.updatedAt).format("DD/MM/YY HH:mm")}</span>
                    </div>
                    {getUserDisplayName(failedEntry) && (
                      <span className="pd-timeline__meta">{t("Por {{name}}", { name: getUserDisplayName(failedEntry) })}</span>
                    )}
                    <span className="pd-timeline__reason">
                      <AlertCircle size={13} />
                      {failedEntry.message || t("Error desconocido")}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {/* Registro de auditoría (roles de la mesa de incidencias) */}
      {canAudit && evidencePackage && (
        <PackageLogbook
          packageId={evidencePackage._id}
          isOpen={isLogbookOpen}
          onClose={() => setIsLogbookOpen(false)}
          incidentOrigin={incidentOrigin}
          onIncidentCreated={onIncidentCreated}
        />
      )}

      {/* Evaluación de calidad: reusa el detalle de "Análisis de entregas" */}
      {isAdmin && evidencePackage && (
        <PackageAiDetailModal
          packageId={evidencePackage._id}
          followingNumber={pkg.followingNumber}
          isOpen={showQuality}
          toggle={() => setShowQuality(false)}
        />
      )}

      {/* Modal de contacto del repartidor (solo admin) */}
      {isAdmin && (
        <DriverContactModal
          open={driverModalOpen}
          onClose={() => setDriverModalOpen(false)}
          loading={driverLoading}
          driver={driverInfo}
          name={repartidor}
        />
      )}

      {/* Visor de evidencia — mismo componente del chat de helpdesk, en modo
          galería (flechas + ← → + contador cuando hay más de una foto). */}
      {lightbox && (
        <MediaLightbox
          images={lightbox.images}
          initialIndex={lightbox.index}
          alt={t("Evidencia del pedido {{number}}", { number: pkg.followingNumber })}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Modal de etiqueta (mismo componente remozado del listado) */}
      <Modal
        open={showLabel}
        setOpen={() => setShowLabel(false)}
        bodyClassName="package-detail-modal-wrapper"
        body={<DownloadVoucher defaultData={evidencePackage} onExchangeChanged={handleOrderChanged} />}
      />

      {/* Editar (estado y repartidor) y cancelar: los MISMOS paneles del menú de
          la lista, para que el detalle no obligue a volver a Pedidos a actuar. */}
      {availableActions.has("editar") && (
        <EditOrderDrawer
          isOpen={editOpen}
          closeModal={setEditOpen}
          data={pkg}
          updateData={handleOrderChanged}
        />
      )}
      {availableActions.has("cancelar") && (
        <CancelPackage
          isOpen={cancelOpen}
          closeModal={setCancelOpen}
          defaultData={pkg}
          updateData={handleOrderChanged}
        />
      )}
      {parisPrintConfirm && (
        <UiModal
          open
          size="sm"
          onClose={() => setParisPrintConfirm(false)}
          title={t("¿Imprimir la etiqueta en Paris?")}
          footer={
            <>
              <Button variant="outline" onClick={() => setParisPrintConfirm(false)} disabled={parisLabelBusy}>
                {t("Cancelar")}
              </Button>
              <Button variant="primary" onClick={() => openParisLabel(true)} loading={parisLabelBusy}>
                {t("Imprimir etiqueta")}
              </Button>
            </>
          }
        >
          <p className="text-sm">
            {t(
              "Esta etiqueta aún no se imprimió. Al imprimirla, Paris marca el pedido como listo para despacho y el comprador ya no puede cancelarlo."
            )}
          </p>
        </UiModal>
      )}
      {/* Se monta al abrir: cada apertura parte de la dirección vigente. Un 409
          (el pedido ya cambió de estado) recarga igual que un guardado. */}
      {addressEditOpen && (
        <EditDeliveryAddressModal
          pkg={pkg}
          onClose={() => setAddressEditOpen(false)}
          onSaved={handleOrderChanged}
          onLocked={handleOrderChanged}
        />
      )}
    </div>
  );
}
